/**
 * Theory notes — the reading a student opens when the attempt ladder reaches
 * its third rung ("Read Theory" in the exam).
 *
 * One JSON document per topic or sub-topic, uploaded by CWY in the curriculum
 * console. This schema IS the file format: the console validates an upload
 * against it before it is stored, and the exam renders exactly these fields, so
 * a file that uploads is a file that displays. `docs/THEORY_NOTES.md` explains
 * it for authors, and `THEORY_NOTE_TEMPLATE` below is a working example.
 *
 * Text fields accept inline maths between `$…$` (and display maths between
 * `$$…$$`), which the exam renders with KaTeX. Nothing else is interpreted —
 * there is no HTML, so a note cannot carry a script or a link onto a student's
 * screen.
 *
 * Strict: an unknown key is rejected rather than ignored. A typo such as
 * "section" for "sections" would otherwise upload cleanly and show a student an
 * empty note.
 */
import { z } from 'zod';

/** Written into every file, so a later format can be told apart from this one. */
export const THEORY_NOTE_FORMAT = 'cwy.theory/1';

/** The whole document, serialised. Generous for prose; small for a payload. */
export const THEORY_NOTE_MAX_BYTES = 64 * 1024;

const text = (max: number) => z.string().trim().min(1).max(max);

export const theoryNoteSchema = z
  .object({
    format: z.literal(THEORY_NOTE_FORMAT),
    /** Shown as the drawer's heading. */
    title: text(200),
    /** One short paragraph — what this is and why it matters. */
    summary: text(1500).optional(),
    /** The explanation itself, in reading order. */
    sections: z
      .array(
        z
          .object({
            heading: text(200),
            body: text(6000),
            /** Optional bullets under the paragraph. */
            points: z.array(text(500)).max(20).optional(),
          })
          .strict(),
      )
      .min(1)
      .max(20),
    /** The few things to hold on to. */
    keyPoints: z.array(text(500)).max(15).optional(),
    formulas: z
      .array(
        z
          .object({
            label: text(200),
            /** LaTeX, without the surrounding `$`. */
            expression: text(500),
            note: text(500).optional(),
          })
          .strict(),
      )
      .max(20)
      .optional(),
    examples: z
      .array(
        z
          .object({
            question: text(2000),
            steps: z.array(text(1500)).min(1).max(15),
            answer: text(1000).optional(),
          })
          .strict(),
      )
      .max(10)
      .optional(),
    commonMistakes: z.array(text(500)).max(15).optional(),
    /** A one-line takeaway, printed last. */
    remember: text(500).optional(),
  })
  .strict();

export type TheoryNoteContent = z.infer<typeof theoryNoteSchema>;

export type TheoryNoteLevel = 'CHAPTER' | 'SUBTOPIC';

/** PUT /curriculum/theory — upload or replace the note on one node. */
export const theoryNoteUploadBody = z.object({
  level: z.enum(['CHAPTER', 'SUBTOPIC']),
  nodeId: z.string().min(1),
  fileName: z.string().trim().max(200).optional(),
  // Validated separately with `theoryNoteSchema`, so the error a person sees
  // names the field in THEIR file rather than `content.sections.0.body`.
  content: z.unknown(),
});
export type TheoryNoteUploadBody = z.infer<typeof theoryNoteUploadBody>;

export const theoryNoteQuery = z.object({
  level: z.enum(['CHAPTER', 'SUBTOPIC']),
  nodeId: z.string().min(1),
});

/** The console's view of one node's note. */
export interface TheoryNoteDTO {
  level: TheoryNoteLevel;
  conceptKey: string;
  fileName: string | null;
  updatedAt: string;
  content: TheoryNoteContent;
}

/**
 * What the exam's "Read Theory" drawer receives. `scope` says whether the note
 * is the sub-topic's own or its topic's, which covers it when it has none.
 */
export interface ExamTheoryDTO {
  scope: TheoryNoteLevel;
  nodeName: string;
  content: TheoryNoteContent;
}

/**
 * Turn a validation failure into lines a content author can act on —
 * `sections[2].body: required` rather than a stack of Zod issue objects.
 */
export function describeTheoryNoteErrors(error: z.ZodError): string[] {
  return error.issues.slice(0, 12).map((issue) => {
    const where = issue.path
      .map((part, i) => (typeof part === 'number' ? `[${part}]` : `${i ? '.' : ''}${String(part)}`))
      .join('');
    return `${where || 'file'}: ${issue.message}`;
  });
}

/** A complete, valid note — what "Download template" hands an author. */
export const THEORY_NOTE_TEMPLATE: TheoryNoteContent = {
  format: THEORY_NOTE_FORMAT,
  title: 'Nature of roots of a quadratic equation',
  summary:
    'The discriminant tells you how many real roots $ax^2 + bx + c = 0$ has before you solve it.',
  sections: [
    {
      heading: 'The discriminant',
      body: 'For $ax^2 + bx + c = 0$ with $a \\neq 0$, the discriminant is $D = b^2 - 4ac$. It is the part of the quadratic formula under the square root.',
      points: [
        '$D > 0$: two distinct real roots.',
        '$D = 0$: two equal real roots.',
        '$D < 0$: no real roots.',
      ],
    },
    {
      heading: 'Why it works',
      body: 'The roots are $x = \\dfrac{-b \\pm \\sqrt{D}}{2a}$. A positive $D$ gives two different square roots, zero gives one, and a negative $D$ has no real square root.',
    },
  ],
  keyPoints: ['Write the equation in standard form before reading off $a$, $b$ and $c$.'],
  formulas: [{ label: 'Discriminant', expression: 'D = b^2 - 4ac' }],
  examples: [
    {
      question: 'Find the nature of the roots of $2x^2 - 4x + 3 = 0$.',
      steps: ['$a = 2$, $b = -4$, $c = 3$.', '$D = (-4)^2 - 4(2)(3) = 16 - 24 = -8$.'],
      answer: 'Since $D < 0$, the equation has no real roots.',
    },
  ],
  commonMistakes: ['Dropping the sign of $b$ when squaring a negative coefficient.'],
  remember: 'Read $D$ first — it tells you what kind of answer to expect.',
};
